import { Component, computed, effect, inject, signal, untracked } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { ApiService } from '../../api.service';
import { SkeletonModule } from 'primeng/skeleton';
import { InternalToolbar } from '../../components/internal-toolbar/internal-toolbar';
import { MatIcon } from '../../components/mat-icon/mat-icon';
import { ReactiveIconTextButton } from '../../components/reactive-icon-text-button/reactive-icon-text-button';
import {
  FormGroup,
  FormControl,
  ReactiveFormsModule,
} from '@angular/forms';
import { AutoCompleteCompleteEvent, AutoCompleteModule } from 'primeng/autocomplete';
import { TAGEvent } from '../../../models/tagevent';
import { TAGUser } from '../../../models/taguser';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputTextModule } from 'primeng/inputtext';
import { CheckboxModule } from 'primeng/checkbox';
import { SelectButtonModule } from 'primeng/selectbutton';
import { ButtonModule } from 'primeng/button';
import { FinanceTransactionListItemComponent } from '../../components/finance-transaction-list-item/finance-transaction-list-item';
import { TAGFinanceTransaction } from '../../../models/tagfinances';
import { FinanceTransactionEditDialog } from './financeTransactionEditDialog';
import {
  ActionConfirmationDialog,
  ActionConfirmationInfo,
} from '../../components/action-confirmation/action-confirmation-dialog';
import {
  newProgressDialogStep,
  ProgressDialogStep,
  ProgressDialogStepState,
  openProgressDialog,
} from '../../components/progressDialog/progressDialog';
import { dialogConfig } from '../../dialogs';
import { Router } from '@angular/router';
import { FinanceTransactionListTotalItemComponent } from '../../components/finance-transaction-list-totalitem/finance-transaction-list-totalitem';
import { InputIconModule } from 'primeng/inputicon';
import { IconFieldModule } from 'primeng/iconfield';
import { mapDateToSchoolyear } from '../../utils';

interface FilterValue {
  reason?: string | null;
  user?: TAGUser | string | null;
  event?: TAGEvent | string | null;
  income?: boolean | null;
  pending?: boolean | null;
}

@Component({
  selector: 'app-internal-finances',
  imports: [
    InternalToolbar,
    ReactiveIconTextButton,
    TranslatePipe,
    SkeletonModule,
    MatIcon,
    ReactiveFormsModule,
    FloatLabelModule,
    InputTextModule,
    InputIconModule,
    IconFieldModule,
    AutoCompleteModule,
    CheckboxModule,
    SelectButtonModule,
    ButtonModule,
    FinanceTransactionListItemComponent,
    FinanceTransactionListTotalItemComponent,
  ],
  providers: [DialogService, TranslateService],
  templateUrl: './internal-finances.html',
  styleUrl: './internal-finances.scss',
})
export class InternalFinances {
  private dialogService = inject(DialogService);
  private translate = inject(TranslateService);
  private messageService = inject(MessageService);
  private router = inject(Router);
  //private route = inject(ActivatedRoute);
  public api = inject(ApiService);

  isMobile() { return window.innerWidth <= 1700; }

  filterForm = new FormGroup({
    reason: new FormControl<string>(null),
    user: new FormControl<TAGUser>(null),
    event: new FormControl<TAGEvent>(null),
    income: new FormControl<boolean>(null),
    pending: new FormControl<boolean>(null),
  });

  users = signal<TAGUser[]>([]);
  loadingUsers = signal(true);
  filteredUsers = signal<TAGUser[]>([]);
  events = signal<TAGEvent[]>([]);
  loadingEvents = signal(true);
  filteredEvents = signal<TAGEvent[]>([]);

  transactions = signal<TAGFinanceTransaction[]>([]);
  loadingTransactions = signal(true);
  filteredTransactions = computed(() => {
    let filterValue = this.filterValue();
    let transactions = this.transactions();
    let users = this.users();
    let events = this.events();

    return untracked(() => {
      if (!filterValue) return transactions;

      let filtered = transactions.filter((t) => {
        if (filterValue.reason) {
          if (!t.reason.toLowerCase().includes(filterValue.reason.toLowerCase())) return false;
        }

        if (filterValue.user != null) {
          let tUser = users.find((u) => u.username === t.user);

          if (typeof filterValue.user === 'string') {
            if (!tUser) {
              if (!t.user || !t.user.toLowerCase().includes(filterValue.user.toLowerCase())) return false;
            } else if (
              !tUser.username.toLowerCase().includes(filterValue.user.toLowerCase()) &&
              !tUser.fullname.toLowerCase().includes(filterValue.user.toLowerCase())
            )
              return false;
          } else if (filterValue.user instanceof TAGUser) {
            if (!tUser) return false;
            else if (tUser._id !== filterValue.user._id) return false;
          }
        }

        if (filterValue.event != null) {
          let tEvent = events.find((e) => e._id === t.event);

          if (typeof filterValue.event === 'string') {
            if (!tEvent) {
              if (!t.event || !t.event.toLowerCase().includes(filterValue.event.toLowerCase())) return false;
            } else if (
              !tEvent.name.toLowerCase().includes(filterValue.event.toLowerCase()) &&
              !tEvent.location.toLowerCase().includes(filterValue.event.toLowerCase())
            )
              return false;
          } else if (filterValue.event instanceof TAGEvent) {
            if (!tEvent) return false;
            else if (tEvent._id !== filterValue.event._id) return false;
          }
        }

        if (filterValue.income != null) {
          if (t.isIncome !== filterValue.income) return false;
        }

        if (filterValue.pending != null) {
          if (t.pending !== filterValue.pending) return false;
        }

        return true;
      });

      return filtered;
    });
  });

  selectedTransactions = signal<string[]>([]);
  filterValue = signal<FilterValue>(null);

  totalAmount = computed(() => {
    if (this.loadingTransactions()) return undefined;
    return this.sum(this.filteredTransactions().filter((t) => !t.pending));
  });

  totalAmountPending = computed(() => {
    if (this.loadingTransactions()) return undefined;
    return this.sum(this.filteredTransactions());
  });

  sum(transactions: TAGFinanceTransaction[]) {
    var total = 0;
    for (let t of transactions) {
      let nAmount = typeof t.amount == 'string' ? parseFloat(t.amount) : t.amount;
      total += t.isIncome ? nAmount : -nAmount;
    }
    return total;
  }

  transactionSchoolyear(transaction: TAGFinanceTransaction) {
    return mapDateToSchoolyear(transaction.date);
  }

  differentSchoolyear(a: TAGFinanceTransaction, b: TAGFinanceTransaction) {
    return this.transactionSchoolyear(a) != this.transactionSchoolyear(b);
  }

  constructor() {
    effect(() => {
      if (!this.loadingEvents() && !this.loadingUsers()) this.loadTransactions();
    });
  }

  ngOnInit(): void {
    this.api.waitForAPI().then(() => {
      this.api
        .getUsers()
        .then((users: TAGUser[]) => {
          this.users.set(users);
          this.loadingUsers.set(false);
        })
        .catch((reason) => {
          this.messageService.add({
            severity: 'warn',
            summary: this.translate.translate("errors.couldNotLoadUsers")(),
            detail: reason.message,
          });
        });

      this.api
        .getEvents()
        .then((events: TAGEvent[]) => {
          this.events.set(events);
          this.loadingEvents.set(false);
        })
        .catch((reason) => {
          this.messageService.add({
            severity: 'warn',
            summary: this.translate.translate("errors.couldNotLoadEvents")(),
            detail: reason.message,
          });
        });
    });

    this.filterForm.valueChanges.subscribe((value) => {
      console.log('Filters changed: ', value);
      this.filterValue.set(value);
    });
  }

  _searchUser(event: AutoCompleteCompleteEvent) {
    this.filteredUsers.set(
      this.users().filter(
        (user) =>
          user.username.toLowerCase().includes(event.query.toLowerCase()) ||
          user.firstname.toLowerCase().includes(event.query.toLowerCase()) ||
          user.lastname.toLowerCase().includes(event.query.toLowerCase()) ||
          user.fullname.toLowerCase().includes(event.query.toLowerCase()) ||
          user.email.toLowerCase().includes(event.query.toLowerCase()),
      ),
    );

    console.log(event.query, this.filteredUsers());
  }

  _searchEvent(event: AutoCompleteCompleteEvent) {
    this.filteredEvents.set(
      this.events().filter((evt) => evt.name.toLowerCase().includes(event.query.toLowerCase())),
    );

    console.log(event.query, this.filteredEvents());
  }

  loadTransactions() {
    this.loadingTransactions.set(true);
    this.transactions.set(Array.from({ length: 3 }).map((_, i) => new TAGFinanceTransaction()));

    this.api
      .getFinanceTransactions()
      .then((transactions: TAGFinanceTransaction[]) => {
        transactions = transactions.sort((a, b) => {
          return b.date.getTime() - a.date.getTime();
        });

        this.transactions.set(transactions);
        this.loadingTransactions.set(false);
      })
      .catch((reason) => {
        this.messageService.add({
          severity: 'errir',
          summary: this.translate.translate("errors.couldNotLoadFinanceTransactions")(),
          detail: reason.message,
        });
      });
  }

  selectTransaction(transaction: TAGFinanceTransaction) {
    this.selectedTransactions.update((s) => {
      if (s.includes(transaction._id)) {
        s.splice(s.indexOf(transaction._id), 1);
      } else {
        s.push(transaction._id);
      }
      return [...s];
    });
  }

  startInvoice() {
    this.router.navigate(['/internal/invoice'], {
      state: {
        user: this.api.user().username,
        availableItems: this.transactions().filter(
          (t) => this.selectedTransactions().includes(t._id) && !t.isIncome,
        ),
      },
    });
  }

  editTransaction(transaction?: TAGFinanceTransaction) {
    const dialogRef = this.dialogService.open(
      FinanceTransactionEditDialog,
      dialogConfig<{ transaction: TAGFinanceTransaction }>({
        data: {
          transaction: transaction,
        },
      }),
    );

    dialogRef.onClose.subscribe((result) => {
      if (result) this.loadTransactions();
    });
  }

  deleteTransaction(transaction: TAGFinanceTransaction) {
    ActionConfirmationDialog.openDialog(
      this.dialogService,
      new ActionConfirmationInfo(
        this.translate.translate('internal.finances.deleteTransactionTitle', {
          transaction: transaction,
        })(),
        this.translate.translate('internal.finances.deleteTransactionMessage', {
          transaction: transaction,
        })(),
        ActionConfirmationInfo.yesno_ydanger,
      ),
    ).then((action: string) => {
      if (action === 'yes') {
        let deleteStep = newProgressDialogStep(
          new ProgressDialogStep(
            ProgressDialogStepState.WAITING,
            this.translate.translate('internal.finances.deleteTransactionProgress')(),
          ),
        );

        let progressDialog = openProgressDialog(this.dialogService, [deleteStep]);

        progressDialog.onChildComponentLoaded.subscribe(async (pDialog) => {
          deleteStep.update((s) => s.setState(ProgressDialogStepState.PROCESSING));

          try {
            await this.api.deleteFinanceTransaction(transaction._id);
            deleteStep.update((s) => s.setState(ProgressDialogStepState.SUCCESS));
            this.loadTransactions();
            await pDialog.finishDialog();
          } catch (reason) {
            deleteStep.update((s) => s.setState(ProgressDialogStepState.ERROR));
            deleteStep.update((s) => s.setSubtitle(reason.message));
            pDialog.finishDialog(-1);
            console.log(reason);
            return;
          }
        });
      }
    });
  }
}
